import { useSelector } from 'react-redux';

import { selectIsReplay } from '@/modules/tryIt/redux/selectors';
import {
  AuthEventType,
  DebugStepType,
  SynthesisRequestCode,
  SynthesisResponseCode,
} from '@/shared/types/tryIt';

import { isMessageFromKnowledge } from '../utils/helper';

export type ResponseTypeFlags = {
  isBroadcastResponse: boolean;
  isCampaignResponse: boolean;
  isAgentResponse: boolean;
  isBrainResponse: boolean;
  isUnknownResponse: boolean;
  isCollectionResponse: boolean;
  isErrorResponse: boolean;
  isInstructionsResponse: boolean;
  isInterruptedResponse: boolean;
  isVoicemailDetectedResponse: boolean;
  isAuthenticationContinueResponse: boolean;
  isAIResponse: boolean;
  isToolResponse: boolean;
};

export const useResponseTypeFlags = ({ index, node }): ResponseTypeFlags => {
  const isReplay = useSelector(selectIsReplay);

  // Check for interrupted/voicemail response first - intents: null is expected for these responses
  const isInterruptedResponse =
    node?.collection?.response_code === SynthesisResponseCode.INTERRUPTED;
  const isVoicemailDetectedResponse =
    node?.collection?.response_code === SynthesisResponseCode.VOICEMAIL_DETECTED;

  const isAuthenticationContinueResponse =
    node?.collection?.response_code === SynthesisResponseCode.AUTHENTICATION_CONTINUE;

  // Auth flow terminal turn (e.g. `Auth_completed` event). Carries no response code
  // and no intents, so the `intents === null` heuristic below would otherwise flag it
  // as an error. Detected by the AUTH_EXIT step the dialog emits on termination.
  const isAuthenticationExitResponse = node?.steps?.some(
    (step) => step?.type === DebugStepType.AUTH && step?.auth?.event === AuthEventType.AUTH_EXIT
  );

  // An AI Response sub-agent produced the turn via its own Synthesis call. It emits a
  // dedicated AI_RESPONSE debug step. Such a turn looks like a synthesis answer with no
  // fragments, so the knowledge/collection heuristic (isMessageFromKnowledge) would
  // otherwise misroute it to CollectionDetailsSection. Detect it explicitly so its
  // Details (auth steps + the AI_RESPONSE deep-link) render via BrainDetailsSection.
  const isAIResponse =
    node?.steps?.some((step) => step?.type === DebugStepType.AI_RESPONSE) ?? false;

  // Planner-driven turns log intents: null with debug populated — not an error
  const isAgenticResponse = Boolean(
    node?.planner_intents?.length || node?.steps?.length || node?.nodes_stack?.length
  );

  const hasExplicitErrorMessage = node?.messages.some((message) => message.type === 'error');
  const hasUnexpectedNullIntents =
    !isAgenticResponse &&
    node?.messages.some(
      (message) => message.intents === null && !message.reminder && !message.isFlowResponse
    );

  // Excluded response types are expected to have intents: null
  const hasError =
    !isInterruptedResponse &&
    !isVoicemailDetectedResponse &&
    !isAuthenticationContinueResponse &&
    !isAuthenticationExitResponse &&
    !isAIResponse &&
    (hasExplicitErrorMessage || hasUnexpectedNullIntents);

  // If there is an error, no need to check any other conditions
  if (hasError) {
    return {
      isBroadcastResponse: false,
      isCampaignResponse: false,
      isAgentResponse: false,
      isBrainResponse: false,
      isInstructionsResponse: false,
      isUnknownResponse: false,
      isCollectionResponse: false,
      isErrorResponse: true,
      isInterruptedResponse: false,
      isVoicemailDetectedResponse: false,
      isAuthenticationContinueResponse: false,
      isAIResponse: false,
      isToolResponse: false,
    };
  }

  const isToolResponse = node?.author_type === 'tool';

  let isUnknownResponse = false;

  if (
    node?.collection?.request_code === SynthesisRequestCode.DIALOG_UNKNOWN &&
    (node?.collection?.response_code === SynthesisResponseCode.NO_SOURCES ||
      node?.collection?.response_code === SynthesisResponseCode.NO_ANSWER ||
      node?.collection?.response_code === SynthesisResponseCode.NO_ANSWER_OUT_OF_SCOPE ||
      node?.collection?.response_code === SynthesisResponseCode.NO_ANSWER_LENGTH_EXCEEDED)
  ) {
    isUnknownResponse = true;
  } else {
    const initialNodeId = isReplay ? node?.id : node?.steps?.[0]?.node_id;

    if (initialNodeId) {
      const nodeType = node?.nodes_stack?.find((node) => node.node_id === initialNodeId)?.type;

      // Don't treat as unknown if response_code is FORCE_WORKFLOW or VOICEMAIL_DETECTED
      // (LLM decided to execute a workflow directly or detected voicemail - these are known response types)
      if (
        nodeType === 'unknown' &&
        node?.collection?.response_code !== SynthesisResponseCode.FORCE_WORKFLOW &&
        node?.collection?.response_code !== SynthesisResponseCode.VOICEMAIL_DETECTED &&
        node?.collection?.response_code !== SynthesisResponseCode.AUTHENTICATION_CONTINUE &&
        !isAuthenticationExitResponse
      ) {
        isUnknownResponse = true;
      }
    }
  }

  const isAgentResponse = node?.author_type === 'agent';
  const isBrainResponse = typeof node?.name === 'string' && !isUnknownResponse && !isToolResponse;
  const isBroadcastResponse = node?.author_type === 'broadcast';
  const isCampaignResponse = node?.author_type === 'campaign';
  // Consider it a collection response if we have fragments AND either:
  // 1. response_code is OK (0), OR
  // 2. response_code is not set (null/undefined) - happens when TryIt backend doesn't populate it
  const isCollectionResponse =
    node?.collection?.fragments?.length > 0 &&
    (node?.collection?.response_code === SynthesisResponseCode.OK ||
      node?.collection?.response_code == null);

  const isInstructionsResponse = isMessageFromKnowledge(node?.collection);

  return {
    isBroadcastResponse,
    isCampaignResponse,
    isAgentResponse,
    isBrainResponse,
    isInstructionsResponse,
    isUnknownResponse,
    isCollectionResponse,
    isErrorResponse: hasError,
    isInterruptedResponse,
    isVoicemailDetectedResponse,
    isAuthenticationContinueResponse,
    isAIResponse,
    isToolResponse,
  };
};
